import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityDisplayName
} from '@backstage/plugin-catalog-react';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete, {
} from '@material-ui/lab/Autocomplete';
import React, { useCallback } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { FieldValidation } from '@rjsf/utils';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

export const MultiRelatedEntityPicker = ({
  onChange,
  schema: { title = 'Entity', description = 'An entity from the catalog' },
  required,
  uiSchema,
  rawErrors,
  formData,
  idSchema,
  formContext,
  disabled,
  readonly 
}: FieldExtensionComponentProps<string[]>) => {

  // Get variables from props
  const allowedKinds = uiSchema['ui:options']?.allowedKinds || [];
  const allowedRelations = uiSchema['ui:options']?.allowedRelations ||   []
  const targetName = String(uiSchema['ui:options']?.targetName)
  const metadataAttribute = uiSchema['ui:options']?.metadataAttribute
  const attributeValue = uiSchema['ui:options']?.attributeValue

  // Get value of the target from the form
  const target = formContext?.formData[targetName] || undefined

  // Convert them if needed
  const entityTypes = Array.isArray(allowedKinds) ? allowedKinds : [allowedKinds];
  const restrictedKinds = entityTypes.map(item => item.toLowerCase());
  const relations = Array.isArray(allowedRelations) ? allowedRelations : [allowedRelations];
  const metadataAttributeName = metadataAttribute !== undefined ? String(metadataAttribute) : metadataAttribute
  const metadataAttributeValue = attributeValue !== undefined ? String(attributeValue) : attributeValue

  // Get full entity from previously selected entity
  const catalogApi = useApi(catalogApiRef);
  const { value: entity } = useAsync(async () => {
    const [kind, namespace, name] = target.split(/[:/]/);
    
    return catalogApi.getEntityByRef({
      kind,
      namespace: namespace || 'default',
      name,
    });
  }, [target]);

  // Extract relations with wanted relation types
  const entityRelations = entity?.relations?.filter(relation => relations.includes(relation.type)) || [];
  
  // Get all entities related
  const { value: entities } = useAsync(async () => {
    const fetchEntityPromises = entityRelations
      .map(relation => {
        let [kind, namespace, name] = relation.targetRef.split(/[:/]/);
        if (restrictedKinds.includes(kind)) {
          const entity = catalogApi.getEntityByRef({
            kind,
            namespace: namespace || 'default',
            name,
          });

          return entity
        }
        return null;
      })
      .filter(Boolean); // Remove null values

    return Promise.all(fetchEntityPromises);
  }, [entity]); 

  // Ensure every entity is valid
  const validEntities = (entities || []).filter(
    (entity): entity is Entity => entity !== null && entity !== undefined
  );
  let entitiesRefs: string[] = [];

  // If metadataAttribute and attributeValue are defined, filter from it's metadata value
  if (metadataAttributeName && metadataAttributeValue) {
    const finalEntities = validEntities.filter(entity => {
      const attribute = entity.metadata[metadataAttributeName]
      if (contains(attribute, metadataAttributeValue)) {
        return true;
      }

      return false;
    })
    entitiesRefs = finalEntities.map(entity => stringifyEntityRef(entity))
  } else {
    entitiesRefs = validEntities.map(entity => stringifyEntityRef(entity))
  }

  const onSelect = useCallback(
    (_: any, selectedRefs: string[]) => {
      const values = selectedRefs
        .map(ref => {
          if (typeof ref !== 'string') {
            // if ref does not exist: pass 'undefined' to trigger validation for required value
            return ref ? stringifyEntityRef(ref as Entity) : undefined;
          }

          return ref;
        })
        .filter(ref => ref !== undefined) as string[];

      onChange(values);
    },
    [onChange, formData]
  );

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      disabled={disabled || readonly}
    >
      <Autocomplete
        multiple
        filterSelectedOptions
        disabled={readonly || disabled || entitiesRefs?.length < 1}
        id={idSchema?.$id}
        onChange={onSelect}
        options={entitiesRefs}
        renderOption={(option) =>  <EntityDisplayName entityRef={option} />}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return '';
        }}
        autoSelect
        renderInput={(params) => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            helperText={description}
            FormHelperTextProps={{
              margin: 'dense',
              style: { marginLeft: 0 },
            }}
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              required: required,
            }}
          />
        )}
      />
    </FormControl>
  );
};

export const validateMultiRelatedEntityPickerValidation = (
  values: string[],
  validation: FieldValidation,
) => {
  values.forEach(value => {
    try {
      parseEntityRef(value);
    } catch {
      validation.addError(`${value} is not a valid entity ref`);
    }
  });
};

// Function that returns a boolean representing if the container contains the value without knowing it's type 
function contains(container: any, value: string): boolean {
  if (typeof container === 'string') {
    return container.includes(value);
  } else if (Array.isArray(container)) {
    return container.some(item => contains(item, value));
  } else if (typeof container === 'object' && container !== null) {
    return Object.values(container).some(item => contains(item, value));
  }
  
  return false;
}